export const RULE_FROM_YEAR = 2027;

const MONTHS_RU = [
  "",
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

export function overlapIsInRuleYears(start, end) {
  const endYear = Number(String(end || "").slice(0, 4));
  return Number.isFinite(endYear) && endYear >= RULE_FROM_YEAR;
}

export function periodsOverlap(startA, endA, startB, endB) {
  if (!startA || !endA || !startB || !endB) return false;
  return startA <= endB && startB <= endA;
}

export function overlapRange(startA, endA, startB, endB) {
  if (!periodsOverlap(startA, endA, startB, endB)) return null;
  return {
    start: startA > startB ? startA : startB,
    end: endA < endB ? endA : endB,
  };
}

export function conflictIdentity(conflict) {
  const a = Number(conflict.employee_a_id);
  const b = Number(conflict.employee_b_id);
  return `${conflict.client_id}:${Math.min(a, b)}:${Math.max(a, b)}:${conflict.overlap_start}:${conflict.overlap_end}`;
}

export function findConflicts(coverage, employees) {
  const byId = new Map();
  (employees || []).forEach((emp) => {
    if (emp?.employee_id == null) return;
    byId.set(Number(emp.employee_id), emp);
  });

  const conflicts = [];
  const seen = new Set();

  (coverage || []).forEach((cover) => {
    const ids = [...new Set((cover.employee_ids || []).map(Number))].sort((x, y) => x - y);
    for (let i = 0; i < ids.length; i += 1) {
      for (let j = i + 1; j < ids.length; j += 1) {
        const empA = byId.get(ids[i]);
        const empB = byId.get(ids[j]);
        (empA?.periods || []).forEach((periodA) => {
          (empB?.periods || []).forEach((periodB) => {
            const window = overlapRange(periodA.start, periodA.end, periodB.start, periodB.end);
            if (!window || !overlapIsInRuleYears(window.start, window.end)) return;
            const item = {
              client_id: cover.client_id,
              client_name: cover.client_name,
              employee_a_id: ids[i],
              employee_b_id: ids[j],
              employee_a_name: empA?.name || String(ids[i]),
              employee_b_name: empB?.name || String(ids[j]),
              overlap_start: window.start,
              overlap_end: window.end,
            };
            const key = conflictIdentity(item);
            if (seen.has(key)) return;
            seen.add(key);
            conflicts.push(item);
          });
        });
      }
    }
  });

  return conflicts.sort((left, right) => {
    const byName = String(left.client_name).localeCompare(String(right.client_name), "ru");
    if (byName !== 0) return byName;
    if (left.overlap_start < right.overlap_start) return -1;
    if (left.overlap_start > right.overlap_start) return 1;
    return left.employee_a_id - right.employee_a_id;
  });
}

export function newConflicts(current, proposed) {
  const existing = new Set((current || []).map(conflictIdentity));
  return (proposed || []).filter((item) => !existing.has(conflictIdentity(item)));
}

export function formatOverlapRange(start, end) {
  if (!start || !end) return "";
  const startDate = parseIsoDate(start);
  const endDate = parseIsoDate(end);
  if (!startDate || !endDate) return `${start} – ${end}`;
  if (startDate.getTime() === endDate.getTime()) return formatDay(startDate);
  if (
    startDate.getMonth() === endDate.getMonth()
    && startDate.getFullYear() === endDate.getFullYear()
  ) {
    return `${startDate.getDate()}–${endDate.getDate()} ${MONTHS_RU[startDate.getMonth() + 1]}`;
  }
  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${formatDay(startDate)} – ${formatDay(endDate)}`;
  }
  return `${formatDay(startDate)} ${startDate.getFullYear()} – ${formatDay(endDate)} ${endDate.getFullYear()}`;
}

export function formatConflictLine(conflict) {
  const span = formatOverlapRange(conflict.overlap_start, conflict.overlap_end);
  return (
    `у клиента «${conflict.client_name}» пересекаются `
    + `${conflict.employee_a_name} и ${conflict.employee_b_name} (${span})`
  );
}

export function employeeConflictKind(employeeId, conflicts, fresh) {
  const hits = matchingEmployeeConflicts(employeeId, conflicts);
  if (hits.length === 0) return null;
  return hits.some((item) => isFreshConflict(item, fresh)) ? "new" : "existing";
}

export function periodConflictKind(employeeId, period, conflicts, fresh) {
  const hits = matchingEmployeeConflicts(employeeId, conflicts).filter((item) => (
    periodsOverlap(period.start, period.end, item.overlap_start, item.overlap_end)
  ));
  if (hits.length === 0) return null;
  return hits.some((item) => isFreshConflict(item, fresh)) ? "new" : "existing";
}

function matchingEmployeeConflicts(employeeId, conflicts) {
  const id = Number(employeeId);
  return (conflicts || []).filter(
    (item) => Number(item.employee_a_id) === id || Number(item.employee_b_id) === id,
  );
}

function isFreshConflict(conflict, fresh) {
  const key = conflictIdentity(conflict);
  return (fresh || []).some((item) => conflictIdentity(item) === key);
}

function parseIsoDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatDay(value) {
  return `${value.getDate()} ${MONTHS_RU[value.getMonth() + 1]}`;
}
